/**
 * The canonical role chain, mirrored from backend/core/rbac_hierarchy.py.
 * Keep the order identical — a tier check on the client must agree with the
 * server or the UI shows a door that opens onto a 403.
 *
 * The map below is a bootstrap default only. `hydrateRoleTiers()` replaces it
 * with the server's canonical registry (`GET /api/admin/roles/tiers`, derived
 * from core/role_registry.py) as soon as the admin shell loads — iter 569.
 */
export const ROLE_ORDER = [
  'super_admin',
  'platform_admin',
  'org_admin',
  'manager',
  'operator',
  'viewer',
];

/**
 * Legacy permissions.py vocabulary → canonical tier. Mirrored from
 * backend/core/rbac_hierarchy.py::LEGACY_ROLE_TO_TIER. Without this a
 * church_admin resolved to "unknown" and the UI showed no capabilities.
 */
export const LEGACY_ROLE_TO_TIER = {
  owner: 'super_admin',
  hq_admin: 'org_admin',
  tenant_admin: 'org_admin',
  church_admin: 'org_admin',
  school_admin: 'org_admin',
  admin: 'org_admin',
  province_admin: 'manager',
  district_admin: 'manager',
  events_admin: 'manager',
  security_admin: 'manager',
  finance_admin: 'manager',
  housing_admin: 'manager',
  church_admin_l3: 'manager',
  school_admin_l3: 'manager',
  finance_officer: 'manager',
  registrar: 'manager',
  event_admin: 'manager',
  event_manager: 'manager',
  finance: 'manager',
  principal: 'manager',
  head_teacher: 'manager',
  hr_admin: 'manager',
  safeguarding_officer: 'manager',
  check_in_staff: 'operator',
  security_staff: 'operator',
  registration_staff: 'operator',
  dining_staff: 'operator',
  accommodation_staff: 'operator',
  exam_proctor: 'operator',
  teacher: 'operator',
  staff: 'operator',
  volunteer: 'operator',
  event_team: 'operator',
  campaign_team: 'operator',
  studio_team: 'operator',
  fwtv_team: 'operator',
  security: 'operator',
  member: 'viewer',
  student: 'viewer',
  parent: 'viewer',
  // Ministry offices — these were missing, so a Pastor or Bishop resolved to
  // "unknown" on the client while the server ranked them properly (iter 569).
  archbishop: 'platform_admin',
  bishop: 'org_admin',
  overseer: 'org_admin',
  provincial_overseer: 'org_admin',
  regional_overseer: 'org_admin',
  district_overseer: 'manager',
  pastor: 'manager',
  associate_pastor: 'manager',
  elder: 'operator',
  deacon: 'operator',
  ministry_leader: 'operator',
  department_leader: 'operator',
  home_group_leader: 'operator',
};

/**
 * Church offices beyond the authority ladder — secretaries, treasurers,
 * ministry leads, campus staff and committee posts. Mirrored from
 * backend/core/rbac_hierarchy.py::CHURCH_OFFICE_ROLE_TIERS. Without these an
 * appointed Secretary or Worship Leader resolved to "unknown" on the client
 * and no picker offered them (iter 571).
 */
export const CHURCH_OFFICE_ROLE_TIERS = {
  // global HQ · executive and Bible school
  executive_office: 'org_admin',
  secretary_general: 'org_admin',
  deputy_secretary_general: 'org_admin',
  deputy_registrar: 'operator',
  chaplain: 'operator',
  director_bible_school: 'manager',
  // global HQ · ministry directors
  director_children: 'manager',
  director_youth: 'manager',
  director_men: 'manager',
  director_women: 'manager',
  director_business: 'manager',
  director_finance: 'manager',
  director_media: 'manager',
  director_missions: 'manager',
  director_evangelism: 'manager',
  director_worship: 'manager',
  director_prayer: 'manager',
  director_education: 'manager',
  director_hospitality: 'manager',
  director_security: 'manager',
  director_comms: 'manager',
  director_hr: 'manager',
  director_it: 'manager',
  director_admin: 'manager',
  // province staff
  deputy_provincial_overseer: 'org_admin',
  provincial_administrator: 'manager',
  provincial_finance: 'manager',
  provincial_secretary: 'operator',
  // region / district staff
  assistant_regional_pastor: 'manager',
  regional_secretary: 'operator',
  regional_finance: 'manager',
  assistant_district_pastor: 'manager',
  district_administrator: 'manager',
  district_secretary: 'operator',
  // campus
  campus_pastor: 'manager',
  campus_administrator: 'manager',
  property_manager: 'manager',
  facilities_coordinator: 'operator',
  // assembly / branch office
  branch_administrator: 'manager',
  branch_secretary: 'operator',
  branch_treasurer: 'manager',
  secretary: 'operator',
  treasurer: 'operator',
  // ministry leads
  worship_leader: 'operator',
  worship: 'operator',
  youth_leader: 'operator',
  youth: 'operator',
  childrens_leader: 'operator',
  mens_leader: 'operator',
  womens_leader: 'operator',
  evangelism_leader: 'operator',
  prayer_coordinator: 'operator',
  hospitality_leader: 'operator',
  security_coordinator: 'operator',
  media_coordinator: 'operator',
  usher: 'operator',
  // department
  assistant_dept_leader: 'operator',
  dept_secretary: 'operator',
  dept_treasurer: 'operator',
  // home group
  assistant_hg_leader: 'operator',
  home_group_host: 'operator',
  home_group_secretary: 'operator',
  // committee posts
  chairperson: 'operator',
  vice_chair: 'operator',
  organizer: 'operator',
  advisor: 'viewer',
  ex_officio: 'viewer',
  visitor: 'viewer',
};

/** Server-hydrated `role name → tier`, filled by `hydrateRoleTiers()`. */
let SERVER_ROLE_TIERS = null;

/**
 * Replace the bootstrap map with the canonical registry from the server.
 * Call once when an admin session starts; safe to call again.
 */
export const hydrateRoleTiers = (roleTiers) => {
  if (roleTiers && typeof roleTiers === 'object' && Object.keys(roleTiers).length) {
    SERVER_ROLE_TIERS = roleTiers;
  }
  return SERVER_ROLE_TIERS;
};

export const loadRoleTiers = async (apiUrl, headers) => {
  try {
    const r = await fetch(`${apiUrl}/api/admin/roles/tiers`, { headers });
    if (!r.ok) return null;
    const body = await r.json();
    return hydrateRoleTiers(body.role_tiers);
  } catch (e) {
    return null;        // bootstrap map keeps working
  }
};

export const canonicalRole = (role) => {
  const key = (role || '').trim().toLowerCase();
  if (!key) return null;
  if (ROLE_ORDER.includes(key)) return key;
  if (SERVER_ROLE_TIERS && SERVER_ROLE_TIERS[key]) return SERVER_ROLE_TIERS[key];
  return LEGACY_ROLE_TO_TIER[key] || CHURCH_OFFICE_ROLE_TIERS[key] || null;
};

export const rankOf = (role) => {
  const resolved = canonicalRole(role);
  return resolved === null ? ROLE_ORDER.length : ROLE_ORDER.indexOf(resolved);
};

/** Super admins and platform admins only — the top two tiers. */
export const isPlatformTier = (user) => {
  if (!user) return false;
  if (user.is_super_admin === true) return true;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  if (perms.includes('*') || perms.includes('super_admin') || perms.includes('platform.wildcard')) {
    return true;
  }
  return [user.role, user.global_role, user.platform_role]
    .some((r) => r && rankOf(r) <= rankOf('platform_admin'));
};

/**
 * Does this user reach `minTier` or above? Used by RoleGuard for every
 * /admin/* route (iter 570). Super admins and wildcard permissions always do.
 */
export const meetsTier = (user, minTier) => {
  if (!user || !minTier) return true;
  if (user.is_super_admin === true) return true;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  if (perms.includes('*') || perms.includes('super_admin')) return true;
  // an admin invited onto a single event is contained server-side, not here
  if (user.event_scope?.is_event_scoped) return true;
  const candidates = [user.role, user.global_role, user.platform_role,
                      // a member signed in through a church office carries it here
                      user.office, user.church_role, ...(user.roles || [])]
    .filter(Boolean);
  const best = candidates.reduce((acc, r) => Math.min(acc, rankOf(r)), ROLE_ORDER.length);
  // No usable role on the session (older logins) — let the server decide
  // rather than locking someone out of their own workspace.
  if (best === ROLE_ORDER.length) return true;
  return best <= rankOf(minTier);
};
